/**
 * 已发布模型（Alice 页面 models 的复刻）：对外模型目录、降级链路由工作台、售价与采购价对照、可见性与批量发布。
 *
 * 背景：对外模型名映射到一条降级链（模型 → 渠道 + 上游模型 + 节点参数）。售价按 Alice 口径在模型上配置，
 *   成本由「渠道模型库」的厂商报价算出；本页把两边放在一行里对照，倒挂（卖价低于采购价）整行标红、零毛利标黄。
 * 设计意图：
 *   - 版式对齐 Alice：四张摘要卡、降级监控条、筛选栏、模型表（行内展开路由工作台）。
 *   - 已有能力不丢：完整价目（阶梯、分时、限时、图片 Token、视频）与思考策略仍在「编辑」弹窗（ModelForm），
 *     节点参数（request_overrides）仍在「高级」路由弹窗（RoutesEditor）；工作台只管优先级、权重、启停、增删节点。
 *   - 采购价与毛利由后端 /models/margins 按主路由计算（同一层多节点取最贵一个，保守）。
 * 关键约束：只有 admin 能写；写操作都会生成新配置版本，数据面几秒内换上；没有启用路由的模型调用会失败，行内标红。
 *   本仓没有 Alice 的「自营渠道免成本」「ai_auto 模型卡」「Jev 熔断」，对应入口不提供。
 */
import { IconCopy, IconEdit, IconEye, IconPlus, IconRoute, IconTrash, IconCoin, IconCloudUpload } from '@tabler/icons-react'
import { useMemo, useState } from 'react'
import { catalogApi, type CatalogModel, type MarginRow } from '@ap/api-client/admin-catalog'
import { A } from '@ap/i18n/admin'
import { AG_catalog as G, errorTextCat } from '@ap/i18n/admin-catalog'
import { C } from '@ap/i18n/common'
import { useI18n } from '@ap/i18n'
import { ConfirmModal, Notice, fmtDateTime, useLoad, useToast } from '@ap/ui'
import { Toolbar } from '../alice/kit.tsx'
import { ActionMenu, InfoBox, Logo, SearchBox, SummaryCard, SummaryGrid, fmtContext } from '../alice/kit-catalog.tsx'
import { useAdmin } from '../session.tsx'
import { CopyModal, PublishModal, RepriceModal, VisibilityModal } from './catalog/ModelDialogs.tsx'
import { ModelForm } from './catalog/ModelForm.tsx'
import { RouteWorkbench } from './catalog/RouteWorkbench.tsx'
import { RoutesEditor } from './catalog/RoutesEditor.tsx'
import { KINDS, SYMBOL, num } from './catalog/shared.ts'

const COLS = 9

/** 售价摘要：文字类「输入 / 输出」，图片每张；原币种。 */
function sellText(m: CatalogModel, tImageToken: string, tVideo: string): string {
  const sym = SYMBOL[m.price_currency]
  if (m.kind === 'image') return m.image_billing_mode === 'token' ? tImageToken : `${sym}${num(m.price_per_image)}`
  if (m.kind === 'video') return tVideo
  return `${sym}${num(m.prompt_price)} / ${sym}${num(m.completion_price)}`
}

/** 采购价摘要（USD / 百万 Token 或每张图）：同层多节点已由后端取最贵。 */
function costText(r: MarginRow | undefined): { text: string; markup: string } | null {
  if (!r || !r.cost_usd) return null
  const c = r.cost_usd
  const m = r.markup ?? {}
  const text = 'image' in c ? `$${num(c.image)}` : `$${num(c.prompt)} / $${num(c.completion)}`
  const k = 'image' in m ? m.image : (m.prompt ?? m.completion)
  return { text, markup: k ? `×${num(k)}` : '' }
}

export function Models() {
  const { t, locale } = useI18n()
  const toast = useToast()
  const { isAdmin } = useAdmin()
  const models = useLoad(() => catalogApi.models(), [])
  const channels = useLoad(() => catalogApi.channels(), [])
  const library = useLoad(() => catalogApi.libraryOverview(30), [])
  const margins = useLoad(() => catalogApi.margins(), [])
  const stats = useLoad(() => catalogApi.fallbackStats(30), [])
  const events = useLoad(() => catalogApi.fallbackEvents({ limit: 8 }), [])
  const alert = useLoad(() => catalogApi.alertConfig(), [])

  const [search, setSearch] = useState('')
  const [kind, setKind] = useState('')
  const [channel, setChannel] = useState('')
  const [visibility, setVisibility] = useState('')
  const [open, setOpen] = useState('')
  const [editing, setEditing] = useState<CatalogModel | 'new' | null>(null)
  const [routing, setRouting] = useState<CatalogModel | null>(null)
  const [copying, setCopying] = useState<CatalogModel | null>(null)
  const [vis, setVis] = useState<CatalogModel | null>(null)
  const [deleting, setDeleting] = useState<CatalogModel | null>(null)
  const [reprice, setReprice] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [busy, setBusy] = useState(false)

  const list = models.data?.data ?? []
  const chList = channels.data?.data ?? []
  const libItems = library.data?.data ?? []
  const marginOf = useMemo(() => new Map((margins.data?.data ?? []).map((r) => [r.model_id, r])), [margins.data])
  const channelOf = useMemo(() => new Map(chList.map((c) => [c.id, c])), [chList])

  // 一条路由节点算「可用」：节点启用、渠道启用且没有被熔断
  const nodeUsable = (cid: string, enabled: boolean) => {
    const c = channelOf.get(cid)
    return enabled && !!c && c.enabled && c.health_status !== 'auto_disabled'
  }
  const covered = list.filter((m) => m.routes.some((r) => nodeUsable(r.channel_id, r.enabled))).length
  const nodesTotal = list.reduce((s, m) => s + m.routes.filter((r) => r.enabled).length, 0)
  const nodesHealthy = list.reduce((s, m) => s + m.routes.filter((r) => nodeUsable(r.channel_id, r.enabled)).length, 0)
  const switches = (stats.data?.data ?? []).reduce((s, x) => s + x.switches, 0)
  const allFailed = (stats.data?.data ?? []).reduce((s, x) => s + x.all_failed, 0)

  const inverted = (margins.data?.data ?? []).filter((r) => r.level === 'loss').map((r) => r.model_id)
  const zero = (margins.data?.data ?? []).filter((r) => r.level === 'zero').map((r) => r.model_id)
  const missingCost = (margins.data?.data ?? []).filter((r) => r.missing_cost_routes.length > 0)

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase()
    return list.filter((m) => {
      if (q && !m.id.toLowerCase().includes(q) && !m.display_name.toLowerCase().includes(q) && !m.tags.some((x) => x.toLowerCase().includes(q))) return false
      if (kind && m.kind !== kind) return false
      if (channel && !m.routes.some((r) => r.channel_id === channel)) return false
      if (visibility === 'public' && !m.public) return false
      if (visibility === 'whitelist' && (m.public || m.allowed_accounts.length === 0)) return false
      if (visibility === 'hidden' && (m.public || m.allowed_accounts.length > 0)) return false
      return true
    })
  }, [list, search, kind, channel, visibility])

  const reloadAll = () => {
    models.reload()
    margins.reload()
    library.reload()
    channels.reload()
  }

  const toggleEnabled = async (m: CatalogModel) => {
    setBusy(true)
    try {
      const r = await catalogApi.patchModel(m.id, { enabled: !m.enabled })
      toast.ok(t(A.models.savedVersion, { v: r.config_version }))
      reloadAll()
    } catch (e) {
      console.error('[models] 切换启用失败', e)
      toast.error(e)
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!deleting) return
    setBusy(true)
    try {
      const r = await catalogApi.deleteModel(deleting.id)
      toast.ok(t(G.models.deleted, { v: r.config_version }))
      setDeleting(null)
      reloadAll()
    } catch (e) {
      console.error('[models] 删除失败', e)
      toast.error(errorTextCat(e, locale))
    } finally {
      setBusy(false)
    }
  }

  const contextOf = (m: CatalogModel): number | null => {
    for (const r of m.routes) {
      const hit = libItems.find((x) => x.channel_id === r.channel_id && x.upstream_model === r.upstream_model)
      if (hit?.context_length) return hit.context_length
    }
    return null
  }

  const ac = alert.data

  return (
    <div id="panel-models" className="panel active">
      <SummaryGrid>
        <SummaryCard label={t(G.models.sPublished)} value={list.length.toLocaleString()} hint={t(G.models.sPublishedHint, { n: list.filter((m) => m.enabled).length })} />
        <SummaryCard
          label={t(G.models.sCoverage)}
          value={list.length ? `${Math.round((covered * 100) / list.length)}%` : '—'}
          valueStyle={list.length && covered < list.length ? { color: 'var(--error-text)' } : undefined}
          hint={t(G.models.sCoverageHint, { a: covered, b: list.length })}
        />
        <SummaryCard label={t(G.models.sNodes)} value={`${nodesHealthy} / ${nodesTotal}`} hint={t(G.models.sNodesHint)} />
        <SummaryCard
          label={t(G.models.sFallback)}
          value={switches.toLocaleString()}
          valueStyle={allFailed ? { color: 'var(--error-text)' } : undefined}
          hint={t(G.models.sFallbackHint, { n: allFailed })}
        />
      </SummaryGrid>

      <InfoBox>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>{t(G.models.fbTitle)}</div>
        <div>{t(G.models.fbNote)}</div>
        <div style={{ marginTop: 6, color: 'var(--text-tertiary)' }}>
          {ac
            ? t(G.models.fbAlert, {
                mail: ac.email_configured ? ac.email_masked : t(G.models.fbOff),
                hook: t(ac.webhook_configured ? G.models.fbOn : G.models.fbOff),
                n: ac.all_failed_threshold,
                w: ac.window_minutes,
              })
            : t(G.common.loading)}
        </div>
        {(stats.data?.data ?? []).length > 0 && (
          <div className="cat-chip-row" style={{ marginTop: 8 }}>
            {(stats.data?.data ?? []).slice(0, 8).map((s) => (
              <span key={s.model} className={`badge ${s.all_failed ? 'badge-off' : 'badge-info'}`}>
                {s.model} · {t(G.models.fbChip, { a: s.switches, b: s.all_failed })}
              </span>
            ))}
          </div>
        )}
        {(events.data?.data ?? []).length > 0 && (
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-tertiary)' }}>
            {(events.data?.data ?? []).slice(0, 5).map((ev) => (
              <div key={ev.id}>
                {fmtDateTime(ev.created_at, locale)} · <span className="mono">{ev.model_id}</span> ·{' '}
                {ev.kind === 'all_failed' ? t(G.models.fbEvAll) : t(G.models.fbEvSwitch, { a: ev.primary_channel, b: ev.to_channel })}
              </div>
            ))}
          </div>
        )}
      </InfoBox>

      {inverted.length > 0 && (
        <div className="cat-banner error">
          {t(G.models.bannerInverted, { n: inverted.length })} <span className="mono">{inverted.slice(0, 6).join(', ')}</span>
          {inverted.length > 6 ? ' …' : ''}
        </div>
      )}
      {zero.length > 0 && (
        <div className="cat-banner warning">
          {t(G.models.bannerZero, { n: zero.length })} <span className="mono">{zero.slice(0, 6).join(', ')}</span>
          {zero.length > 6 ? ' …' : ''}
        </div>
      )}
      {missingCost.length > 0 && (
        <div className="cat-banner warning">
          {t(G.models.bannerMissing, { n: missingCost.length })} <span className="mono">{missingCost.slice(0, 4).map((r) => r.model_id).join(', ')}</span>
          {missingCost.length > 4 ? ' …' : ''}
        </div>
      )}
      {!isAdmin && (
        <div style={{ marginBottom: 12 }}>
          <Notice tone="accent">{t(G.common.readOnly)}</Notice>
        </div>
      )}

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder={t(G.models.search)} width={220} />
        <select className="cat-select" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">{t(G.models.allKinds)}</option>
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
        <select className="cat-select" value={channel} onChange={(e) => setChannel(e.target.value)}>
          <option value="">{t(G.library.allChannels)}</option>
          {chList.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name || c.id}
            </option>
          ))}
        </select>
        <select className="cat-select" value={visibility} onChange={(e) => setVisibility(e.target.value)}>
          <option value="">{t(G.models.allVisibility)}</option>
          <option value="public">{t(G.models.visOnlyPublic)}</option>
          <option value="whitelist">{t(G.models.visOnlyWhitelist)}</option>
          <option value="hidden">{t(G.models.visOnlyHidden)}</option>
        </select>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-tertiary)' }}>{t(G.models.count, { a: shown.length, b: list.length })}</span>
        {isAdmin && (
          <>
            <button className="btn btn-secondary btn-sm" style={{ whiteSpace: 'nowrap' }} onClick={() => setReprice(true)}>
              <IconCoin size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
              {t(G.models.reprice)}
            </button>
            <button className="btn btn-secondary btn-sm" style={{ whiteSpace: 'nowrap' }} onClick={() => setPublishing(true)}>
              <IconCloudUpload size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
              {t(G.models.publish)}
            </button>
            <button className="btn btn-primary btn-sm" style={{ whiteSpace: 'nowrap' }} onClick={() => setEditing('new')}>
              <IconPlus size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
              {t(A.models.create)}
            </button>
          </>
        )}
      </Toolbar>

      <div className="table-wrap">
        <div className="table-inner">
          <table>
            <thead>
              <tr>
                <th>{t(G.models.colModel)}</th>
                <th>{t(A.models.kind)}</th>
                <th>{t(G.models.colRoutes)}</th>
                <th>{t(A.models.sellPrice)}</th>
                <th title={t(G.models.colCostHint)}>{t(G.models.colCost)}</th>
                <th>{t(G.models.colContext)}</th>
                <th>{t(G.models.colVisibility)}</th>
                <th>{t(C.status)}</th>
                <th>{t(G.library.colActions)}</th>
              </tr>
            </thead>
            <tbody>
              {models.error ? (
                <tr>
                  <td colSpan={COLS} className="empty" style={{ textAlign: 'center', padding: 32, color: 'var(--error-text)' }}>
                    {t(G.common.loadFailed, { msg: errorTextCat(models.error, locale) })}
                  </td>
                </tr>
              ) : models.loading && !models.data ? (
                <tr>
                  <td colSpan={COLS} className="empty" style={{ textAlign: 'center', padding: 32 }}>
                    {t(G.common.loading)}
                  </td>
                </tr>
              ) : shown.length === 0 ? (
                <tr>
                  <td colSpan={COLS} className="empty" style={{ textAlign: 'center', padding: 32, color: 'var(--text-tertiary)' }}>
                    {t(G.models.empty)}
                  </td>
                </tr>
              ) : (
                shown.map((m) => {
                  const mr = marginOf.get(m.id)
                  const cost = costText(mr)
                  const usable = m.routes.filter((r) => nodeUsable(r.channel_id, r.enabled))
                  const enabledNodes = m.routes.filter((r) => r.enabled)
                  const isOpen = open === m.id
                  const rowCls = mr?.level === 'loss' ? 'row-price-inverted' : mr?.level === 'zero' ? 'row-zero-margin' : undefined
                  const ctx = contextOf(m)
                  return (
                    <ModelRows
                      key={m.id}
                      rowCls={rowCls}
                      main={
                        <>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 190 }}>
                              <Logo url={m.icon_url} name={m.display_name || m.id} size="sm" />
                              <div style={{ minWidth: 0 }}>
                                <div className="mono" style={{ fontWeight: 600, fontSize: 13 }}>
                                  {m.id}
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{m.display_name || '—'}</div>
                                {m.tags.length > 0 && (
                                  <div className="cat-chip-row" style={{ marginTop: 4 }}>
                                    {m.tags.map((x) => (
                                      <span key={x} className="badge badge-info">
                                        {x}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="badge badge-info">{m.kind}</span>
                          </td>
                          <td>
                            {enabledNodes.length === 0 ? (
                              <span className="badge badge-off">{t(A.models.noRoutes)}</span>
                            ) : (
                              <button className="btn btn-secondary btn-sm" onClick={() => setOpen(isOpen ? '' : m.id)}>
                                <IconRoute size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                                {t(G.models.routeSummary, { a: usable.length, b: enabledNodes.length })}
                              </button>
                            )}
                            {enabledNodes.length === 0 && isAdmin && (
                              <button className="btn btn-secondary btn-sm" style={{ marginLeft: 6 }} onClick={() => setOpen(isOpen ? '' : m.id)}>
                                {t(A.models.editRoutes)}
                              </button>
                            )}
                          </td>
                          <td>
                            <div className="mono" style={{ whiteSpace: 'nowrap' }}>
                              {sellText(m, t(A.models.imageModeToken), t(A.models.videoPricing))}
                            </div>
                            <div className="cat-chip-row" style={{ marginTop: 3 }}>
                              <span className="badge badge-info">{m.price_currency}</span>
                              {m.pricing_tiers && m.pricing_tiers.length > 0 && <span className="badge badge-info">{t(A.models.tiers)}</span>}
                              {m.peak_pricing && <span className="badge badge-info">{t(A.models.peak)}</span>}
                              {m.promotional_pricing && <span className="badge badge-warn">{t(A.models.promo)}</span>}
                            </div>
                          </td>
                          <td>
                            {cost ? (
                              <div>
                                <span className="mono" style={{ whiteSpace: 'nowrap' }}>
                                  {cost.text}
                                </span>
                                {cost.markup && (
                                  <span style={{ fontSize: 11, marginLeft: 4, color: mr?.level === 'loss' ? 'var(--error-text)' : 'var(--text-tertiary)' }}>{cost.markup}</span>
                                )}
                              </div>
                            ) : (
                              <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t(G.models.costNone[mr?.level ?? 'unknown'] ?? G.models.costNone.unknown)}</span>
                            )}
                            {mr && mr.missing_cost_routes.length > 0 && (
                              <div style={{ fontSize: 11, color: 'var(--error-text)', marginTop: 2 }} title={mr.missing_cost_routes.join('\n')}>
                                {t(G.models.costMissingRoutes, { n: mr.missing_cost_routes.length })}
                              </div>
                            )}
                          </td>
                          <td>{fmtContext(ctx)}</td>
                          <td>
                            {m.public ? (
                              <span className="badge badge-ok">{t(A.models.public)}</span>
                            ) : m.allowed_accounts.length > 0 ? (
                              <span className="badge badge-info">{t(G.models.visWhitelistN, { n: m.allowed_accounts.length })}</span>
                            ) : (
                              <span className="badge badge-off">{t(G.models.visHidden)}</span>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${m.enabled ? 'badge-ok' : 'badge-off'}`}>{t(m.enabled ? A.status.enabled : A.status.disabled)}</span>
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {isAdmin && (
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <button className="btn btn-secondary btn-sm" onClick={() => setEditing(m)}>
                                  <IconEdit size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                                  {t(C.edit)}
                                </button>
                                <ActionMenu label="···">
                                  <button onClick={() => setVis(m)}>
                                    <IconEye size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                                    {t(G.models.menuVisibility)}
                                  </button>
                                  <button onClick={() => setCopying(m)}>
                                    <IconCopy size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                                    {t(G.models.copy)}
                                  </button>
                                  <button disabled={busy} onClick={() => toggleEnabled(m)}>
                                    {t(m.enabled ? G.library.disable : G.library.enable)}
                                  </button>
                                  <button style={{ color: 'var(--error-text)' }} onClick={() => setDeleting(m)}>
                                    <IconTrash size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                                    {t(C.delete)}
                                  </button>
                                </ActionMenu>
                              </div>
                            )}
                          </td>
                        </>
                      }
                      workbench={
                        isOpen && (
                          <RouteWorkbench
                            key={`${m.id}:${m.routes.length}`}
                            model={m}
                            channels={chList}
                            library={libItems}
                            isAdmin={isAdmin}
                            colSpan={COLS}
                            onSaved={reloadAll}
                            onAdvanced={() => setRouting(m)}
                          />
                        )
                      }
                    />
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ModelForm
        model={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          reloadAll()
        }}
      />
      <RoutesEditor
        model={routing}
        channels={chList}
        library={libItems}
        onClose={() => setRouting(null)}
        onSaved={() => {
          setRouting(null)
          reloadAll()
        }}
      />
      <CopyModal
        model={copying}
        onClose={() => setCopying(null)}
        onSaved={() => {
          setCopying(null)
          reloadAll()
        }}
      />
      <VisibilityModal
        model={vis}
        onClose={() => setVis(null)}
        onSaved={() => {
          setVis(null)
          reloadAll()
        }}
      />
      <RepriceModal open={reprice} modelIds={shown.map((m) => m.id)} onClose={() => setReprice(false)} onApplied={reloadAll} />
      <PublishModal open={publishing} onClose={() => setPublishing(false)} onPublished={reloadAll} />
      <ConfirmModal
        open={deleting !== null}
        title={t(G.models.deleteTitle, { id: deleting?.id ?? '' })}
        confirmLabel={t(C.delete)}
        danger
        busy={busy}
        onConfirm={remove}
        onClose={() => setDeleting(null)}
      >
        <span>{t(G.models.deleteHint)}</span>
      </ConfirmModal>
    </div>
  )
}

/** 一个模型占两行：主行与可选的路由工作台行；用 Fragment 包住以便在 tbody 里使用。 */
function ModelRows({ rowCls, main, workbench }: { rowCls: string | undefined; main: React.ReactNode; workbench: React.ReactNode }) {
  return (
    <>
      <tr className={rowCls}>{main}</tr>
      {workbench}
    </>
  )
}
